import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Alert, AlertDescription, AlertTitle } from '../ui/Alert';
import { 
  Bell, 
  Shield, 
  AlertCircle, 
  CheckCircle, 
  XCircle, 
  Info,
  ExternalLink
} from 'lucide-react';

interface BrowserPermissionRequestProps {
  permission: NotificationPermission;
  onPermissionChange: (permission: NotificationPermission) => void;
}

export function BrowserPermissionRequest({ 
  permission, 
  onPermissionChange 
}: BrowserPermissionRequestProps) {
  const [isRequesting, setIsRequesting] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  const requestPermission = async () => {
    if (!('Notification' in window)) {
      return;
    }

    setIsRequesting(true);

    try {
      const result = await Notification.requestPermission();
      onPermissionChange(result);
      
      // Send a test notification if granted
      if (result === 'granted') {
        new Notification('🎉 Notifications Enabled!', {
          body: 'You\'ll now receive helpful reminders and updates.',
          icon: '/favicon.ico'
        });
      }
    } catch (error) {
      console.error('Failed to request notification permission:', error);
    } finally {
      setIsRequesting(false);
    }
  };

  const getPermissionStatus = () => {
    switch (permission) {
      case 'granted':
        return {
          icon: CheckCircle,
          color: 'text-green-600',
          bgColor: 'bg-green-50 dark:bg-green-950/20',
          borderColor: 'border-green-200 dark:border-green-800',
          status: 'Granted',
          description: 'You\'ll receive notifications for important updates'
        };
      case 'denied':
        return {
          icon: XCircle,
          color: 'text-red-600',
          bgColor: 'bg-red-50 dark:bg-red-950/20',
          borderColor: 'border-red-200 dark:border-red-800',
          status: 'Blocked',
          description: 'Notifications are blocked in your browser settings'
        };
      default:
        return {
          icon: AlertCircle,
          color: 'text-orange-600',
          bgColor: 'bg-orange-50 dark:bg-orange-950/20',
          borderColor: 'border-orange-200 dark:border-orange-800',
          status: 'Pending',
          description: 'Permission needed to send notifications'
        };
    }
  };

  const status = getPermissionStatus();
  const StatusIcon = status.icon;

  if (!('Notification' in window)) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Notifications Not Supported</AlertTitle>
        <AlertDescription>
          Your browser doesn't support notifications. Please try using a modern browser 
          like Chrome, Firefox, or Safari.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      <Card className={`${status.bgColor} ${status.borderColor}`}>
        <CardHeader>
          <CardTitle className="flex items-center">
            <StatusIcon className={`h-5 w-5 mr-2 ${status.color}`} />
            Browser Permissions
            <Badge 
              variant={permission === 'granted' ? 'default' : 'destructive'} 
              className="ml-2"
            >
              {status.status}
            </Badge>
          </CardTitle>
          <CardDescription className={status.color}>
            {status.description}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {permission === 'default' && (
            <div className="space-y-4">
              <div className="flex items-start space-x-3 p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
                <Info className="h-5 w-5 text-blue-600 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-blue-800 dark:text-blue-200 mb-1">
                    Why we need permission
                  </p>
                  <ul className="text-blue-700 dark:text-blue-300 space-y-1 text-xs">
                    <li>• Remind you of upcoming scheduled tasks</li>
                    <li>• Alert you about approaching deadlines</li>
                    <li>• Notify you of optimal times to work on your goals</li>
                    <li>• Send achievement celebrations when you complete tasks</li>
                  </ul>
                </div>
              </div>

              <Button 
                onClick={requestPermission} 
                disabled={isRequesting}
                className="w-full"
              >
                <Bell className="h-4 w-4 mr-2" />
                {isRequesting ? 'Requesting Permission...' : 'Allow Notifications'}
              </Button>

              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setShowInstructions(!showInstructions)}
                className="w-full text-xs"
              >
                Need help? Manual setup instructions
                <ExternalLink className="h-3 w-3 ml-1" />
              </Button>
            </div>
          )}

          {permission === 'denied' && (
            <div className="space-y-4">
              <div className="flex items-start space-x-3 p-3 bg-red-50 dark:bg-red-950/20 rounded-lg">
                <Shield className="h-5 w-5 text-red-600 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-red-800 dark:text-red-200 mb-1">
                    Notifications are blocked
                  </p>
                  <p className="text-red-700 dark:text-red-300 text-xs">
                    You've previously denied notification permission. To enable notifications, 
                    you'll need to manually allow them in your browser settings.
                  </p>
                </div>
              </div>

              <Button 
                variant="outline"
                onClick={() => setShowInstructions(true)}
                className="w-full"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Show Setup Instructions
              </Button>
            </div>
          )}

          {permission === 'granted' && (
            <div className="flex items-start space-x-3 p-3 bg-green-50 dark:bg-green-950/20 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-green-800 dark:text-green-200 mb-1">
                  All set! 🎉
                </p>
                <p className="text-green-700 dark:text-green-300 text-xs">
                  You can now configure your notification preferences below. 
                  We'll help you stay on track with your goals and tasks.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Manual Setup Instructions */}
      {showInstructions && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center text-blue-600">
              <Info className="h-5 w-5 mr-2" />
              Manual Setup Instructions
            </CardTitle>
            <CardDescription>
              Follow these steps to enable notifications in your browser
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Chrome Instructions */}
              <div>
                <h4 className="font-medium text-sm mb-2">Google Chrome</h4>
                <ol className="text-xs text-muted-foreground space-y-1 ml-4">
                  <li>1. Click the lock icon in the address bar</li>
                  <li>2. Select "Site settings"</li>
                  <li>3. Find "Notifications" and change to "Allow"</li>
                  <li>4. Refresh this page</li>
                </ol>
              </div>

              {/* Firefox Instructions */}
              <div>
                <h4 className="font-medium text-sm mb-2">Firefox</h4>
                <ol className="text-xs text-muted-foreground space-y-1 ml-4">
                  <li>1. Click the shield icon in the address bar</li>
                  <li>2. Click "Turn off Blocking" next to Notifications</li>
                  <li>3. Or go to Settings → Privacy & Security → Permissions</li>
                  <li>4. Refresh this page</li>
                </ol>
              </div>

              {/* Safari Instructions */}
              <div>
                <h4 className="font-medium text-sm mb-2">Safari</h4>
                <ol className="text-xs text-muted-foreground space-y-1 ml-4">
                  <li>1. Go to Safari → Preferences → Websites</li>
                  <li>2. Select "Notifications" in the left sidebar</li>
                  <li>3. Find this website and change to "Allow"</li>
                  <li>4. Refresh this page</li>
                </ol>
              </div>

              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowInstructions(false)}
                className="w-full"
              >
                Close Instructions
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}